import React from 'react';
import { 
    Box, Grid, Paper, Typography, Chip, Divider, 
    List, ListItem, ListItemIcon, ListItemText 
} from '@mui/material';
import { 
    LocationOn, Event, AccessTime, Phone, Person, 
    CreditCard, Lock, PriorityHigh, Notes 
} from '@mui/icons-material';

const InfoRow = ({ icon, label, value, subValue }) => (
    <ListItem disablePadding sx={{ py: 1.5 }}>
        <ListItemIcon sx={{ minWidth: 40, color: 'text.secondary' }}>
            {icon}
        </ListItemIcon>
        <ListItemText 
            primary={
                <Typography variant="body2" color="text.secondary">
                    {label}
                </Typography>
            }
            secondary={
                <Box>
                    <Typography variant="body1" fontWeight={500} color="text.primary">
                        {value || '-'}
                    </Typography>
                    {subValue && (
                        <Typography variant="caption" color="text.secondary" display="block">
                            {subValue}
                        </Typography>
                    )}
                </Box>
            }
        />
    </ListItem>
);

const JobOverview = ({ job }) => {
    // Format dates
    const scheduledDate = job.scheduled_date 
        ? new Date(job.scheduled_date).toLocaleDateString() 
        : 'Unscheduled';
    const scheduledTime = job.scheduled_date
        ? new Date(job.scheduled_date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        : '';

    return (
        <Grid container spacing={3}>
            {/* Main Info */}
            <Grid item xs={12} md={8}>
                <Paper elevation={0} sx={{ p: 3, border: '1px solid #E2E8F0', borderRadius: 3, mb: 3 }}>
                    <Typography variant="h6" fontWeight={700} gutterBottom>
                        Job Details
                    </Typography>
                    <List>
                        <InfoRow 
                            icon={<Person />} 
                            label="Client" 
                            value={job.client_name || 'No Client'} 
                            subValue={job.client_phone || job.client_email || null}
                        />
                        <InfoRow 
                            icon={<LocationOn />} 
                            label="Location" 
                            value={job.location} 
                        />
                        <InfoRow 
                            icon={<Notes />} 
                            label="Description" 
                            value={job.description} 
                        />
                        <InfoRow 
                            icon={<Event />} 
                            label="Schedule" 
                            value={scheduledDate}
                            subValue={scheduledTime}
                        />
                        <InfoRow 
                            icon={<AccessTime />} 
                            label="Preferences" 
                            value={`${job.time_preference} • ${job.flexibility}`}
                            subValue={job.not_before_date ? `Not before: ${job.not_before_date}` : null}
                        />
                    </List>

                    <Divider sx={{ my: 2 }} />
                    
                    <Typography variant="subtitle2" fontWeight={700} color="text.secondary" sx={{ mb: 2, mt: 1 }}>
                        SITE ACCESS & CONTACT
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <List dense>
                                <InfoRow 
                                    icon={<Lock />} 
                                    label="Access" 
                                    value={job.site_access?.toUpperCase()} 
                                />
                            </List>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <List dense>
                                <InfoRow 
                                    icon={<Person />} 
                                    label="Site Contact" 
                                    value={job.site_contact_name} 
                                    subValue={job.site_contact_phone}
                                />
                            </List>
                        </Grid>
                    </Grid>
                    
                    {job.special_instructions && (
                        <Box sx={{ mt: 2, p: 2, bgcolor: '#FEF2F2', borderRadius: 2, border: '1px dashed #FECACA' }}>
                            <Typography variant="subtitle2" color="error" fontWeight={700} gutterBottom>
                                <PriorityHigh fontSize="inherit" sx={{ mr: 0.5, verticalAlign: 'middle' }} />
                                SPECIAL INSTRUCTIONS
                            </Typography>
                            <Typography variant="body2">
                                {job.special_instructions}
                            </Typography>
                        </Box>
                    )}
                </Paper>

                {/* Worker Notes (if any) */}
                {job.worker_notes && (
                     <Paper elevation={0} sx={{ p: 3, border: '1px solid #E2E8F0', borderRadius: 3 }}>
                        <Typography variant="h6" fontWeight={700} gutterBottom>
                            Worker Field Notes
                        </Typography>
                        <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
                            {job.worker_notes}
                        </Typography>
                     </Paper>
                )}
            </Grid>

            {/* Sidebar Info */}
            <Grid item xs={12} md={4}>
                <Paper elevation={0} sx={{ p: 3, border: '1px solid #E2E8F0', borderRadius: 3, mb: 3 }}>
                    <Typography variant="subtitle2" fontWeight={700} color="text.secondary" gutterBottom>
                        METADATA
                    </Typography>
                    
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="caption" color="text.secondary">Urgency</Typography>
                        <Box sx={{ mt: 0.5 }}>
                            <Chip 
                                label={job.urgency?.toUpperCase()} 
                                color={job.urgency === 'urgent' ? 'error' : job.urgency === 'high' ? 'warning' : 'default'}
                                size="small"
                                sx={{ fontWeight: 600 }}
                            />
                        </Box>
                    </Box>

                    <Box sx={{ mb: 3 }}>
                        <Typography variant="caption" color="text.secondary">Job Type</Typography>
                        <Typography variant="body1" fontWeight={500}>{job.job_type || 'Standard'}</Typography>
                    </Box>

                    <Box sx={{ mb: 3 }}>
                        <Typography variant="caption" color="text.secondary">Info Status</Typography>
                        <Typography variant="body1" fontWeight={500}>{job.info_status}</Typography>
                    </Box>
                </Paper>

                <Paper elevation={0} sx={{ p: 3, border: '1px solid #E2E8F0', borderRadius: 3 }}>
                    <Typography variant="subtitle2" fontWeight={700} color="text.secondary" gutterBottom>
                        BILLING
                    </Typography>
                    
                    {job.bill_to_other ? (
                        <List dense>
                            <InfoRow 
                                icon={<CreditCard />} 
                                label="Bill To" 
                                value={job.billing_name} 
                                subValue={job.billing_email}
                            />
                        </List>
                    ) : (
                        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', mt: 1 }}>
                            Standard billing to Client
                        </Typography>
                    )}
                </Paper>
            </Grid>
        </Grid>
    );
};

export default JobOverview;
